import { useState } from 'react'

import { playRows } from './gameModel.js'
import { sourceAge } from './model.js'

// The last fifty plays, newest first. P6-328b (GN-1b).
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 ABOUT FIFTY, BECAUSE THE BOT HOLDS ABOUT FIFTY — AND THE HEADING SAYS SO
// ═══════════════════════════════════════════════════════════════════════════
//
// `parse_playbyplay` keeps the last `PLAYS_LIMIT` (50) actions. A page that titled this "Plays"
// and drew fifty rows would read as the game's whole play-by-play, so the note beside the heading
// counts what is actually here — `50 plays · NBA · 14s` — and the filter's empty state says "in the
// last fifty", not "in the game". What is not here is not claimed.
//
// ⚠️ NEWEST FIRST, the design's order: what somebody watching wants to read is at the top and does
// not move. The reversal is `gameModel.js::playRows`', done once.
//
// ⚠️ THE FILTER IS TWO CHOICES, `All` AND `Scoring`. The design has a third, "Your players", which is
// fantasy's (GN-6) and is not drawn: with it off, nothing fantasy-shaped renders. A scoring play is
// one the feed marks `made` — a field goal or a free throw that went in — which is the feed's own
// word and not an inference from the score moving.

export default function Plays({ game, source, stampMs, nowMs }) {
  const [filter, setFilter] = useState('all')
  const all = playRows(game)
  const shown = filter === 'scoring' ? all.filter((row) => row.scoring) : all
  return (
    <section className="d2d-hoops__plays" aria-label="Plays" data-testid="hoops-plays">
      <div className="d2d-hoops__playshead">
        <h3 className="d2d-hoops__sectiontitle d2d-micro">Plays · newest first</h3>
        <span className="d2d-hoops__playsnote d2d-micro" data-testid="hoops-plays-note">
          {all.length} {all.length === 1 ? 'play' : 'plays'} · {sourceAge(source, stampMs, nowMs)}
        </span>
      </div>

      <div className="d2d-hoops__filters">
        {[
          ['all', 'All'],
          ['scoring', 'Scoring'],
        ].map(([key, label]) => (
          <button
            type="button"
            key={key}
            className="d2d-hoops__filter"
            aria-pressed={filter === key ? 'true' : 'false'}
            data-testid={`hoops-filter-${key}`}
            onClick={() => setFilter(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <p className="d2d-hoops__noplays">
          {filter === 'scoring' ? 'No scoring plays in the last fifty.' : 'No plays yet.'}
        </p>
      ) : (
        <ol className="d2d-hoops__playlist" data-testid="hoops-playlist">
          {shown.map((row) => (
            <li
              className="d2d-hoops__play"
              key={row.key}
              data-testid="hoops-play"
              data-side={row.side ?? 'none'}
              data-scoring={row.scoring ? 'true' : 'false'}
            >
              <span className="d2d-hoops__playwhen">{row.when}</span>
              <span className="d2d-hoops__playtick" aria-hidden="true" />
              <span className="d2d-hoops__playtext">
                {row.name === null ? null : <span className="d2d-hoops__playwho">{row.name}</span>}
                {row.name === null ? '' : ' '}
                {row.description}
              </span>
              <span className="d2d-hoops__playscore">{row.score ?? ''}</span>
            </li>
          ))}
        </ol>
      )}
    </section>
  )
}
